import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { client } from '../../client';
import type { ImageSource, ImageView } from '../../generated';

/*
 * Hand-written stand-in for POST /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/images/upload, live behind
 * the API's `custom-benchmarks` flag but missing from the spec the generator reads. It creates the image record and
 * answers with a presigned S3 POST; the caller then sends the file to S3 with those fields.
 *
 * `product_category_id` is only sent when the caller has one. Without it the API stores the original as is (JPG,
 * PNG or WEBP, judged by the file name's extension) and neither scores nor resizes it until it is first scored.
 * Tests answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

const UPLOAD_BENCHMARK_IMAGE_URL = '/v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/images/upload';

export interface UploadBenchmarkImageRequest {
  /** The file name. */
  name: string;
  product_category_id?: string;
  source?: ImageSource;
}

export type UploadBenchmarkImageMutationVariables = {
  workspace_id: string;
  benchmark_id: string;
} & UploadBenchmarkImageRequest;

/** Where and how to send the file: the new image's ID and the presigned S3 POST. */
export interface BenchmarkImageUpload {
  imageId: string;
  uploadUrl: string;
  uploadFields: Record<string, string>;
}

export type UploadBenchmarkImageMutation = BenchmarkImageUpload;

export function toBenchmarkImageUpload(image: ImageView): BenchmarkImageUpload {
  if (!image.id || !image.url || !image.upload_fields) {
    throw new Error('The benchmark image upload response is missing its presigned upload.');
  }
  return {
    imageId: image.id,
    uploadUrl: image.url,
    uploadFields: JSON.parse(image.upload_fields) as Record<string, string>,
  };
}

async function uploadBenchmarkImage({
  workspace_id,
  benchmark_id,
  source = 'user_uploaded',
  ...body
}: UploadBenchmarkImageMutationVariables) {
  const { data } = await client.post<{ 200: ImageView }, unknown, true>({
    url: UPLOAD_BENCHMARK_IMAGE_URL,
    path: { workspace_id, benchmark_id },
    body: { ...body, source },
    throwOnError: true,
  });
  return toBenchmarkImageUpload(data);
}

export const useUploadBenchmarkImageMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<UploadBenchmarkImageMutation, TError, UploadBenchmarkImageMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<UploadBenchmarkImageMutation, TError, UploadBenchmarkImageMutationVariables, TContext>({
    mutationKey: ['UploadBenchmarkImage'],
    mutationFn: uploadBenchmarkImage,
    ...options,
  });
};

useUploadBenchmarkImageMutation.fetcher = (variables: UploadBenchmarkImageMutationVariables) => () =>
  uploadBenchmarkImage(variables);
